import api from '@/utils/api';
import { ApiResponse } from "@/utils/api/ApiProps";

export interface FunnelStage {
  stage_id: number;
  uuid: string;
  client_id_fk: number;
  funnel_type: 'sales' | 'marketing' | 'support' | 'custom';
  stage_name: string;
  stage_description?: string;
  stage_order: number;
  is_default: boolean;
  is_active: boolean;
  is_win: boolean;
  is_lost: boolean;
  color_code?: string;
  icon?: string;
  probability_percentage?: number;
  sla_hours?: number;
  created_at: string;
  updated_at: string;
  statusMappings?: StatusMapping[];
}

export interface StatusMapping {
  mapping_id: number;
  uuid: string;
  stage_id_fk: number;
  status_id_fk: number;
  mapping_type: 'direct' | 'pattern' | 'regex' | 'conditional';
  pattern_match?: string;
  is_active: boolean;
  priority: number;
  status?: StatusOption;
}

export interface StatusOption {
  opt_id: number;
  uuid: string;
  opt_name: string;
  opt_value: string;
  opt_active: boolean;
}

export interface FunnelStageFormData {
  stage_name: string;
  stage_description?: string;
  stage_order: number;
  color_code?: string;
  funnel_type?: 'sales' | 'marketing' | 'support' | 'custom';
  is_default?: boolean;
  is_win?: boolean;
  is_lost?: boolean;
  probability_percentage?: number;
  sla_hours?: number;
}

export interface StatusMappingFormData {
  stage_id_fk: number;
  status_id_fk: number;
  mapping_type?: 'direct' | 'pattern';
  priority?: number;
}

export interface MultipleStatusMappingFormData {
  stage_id_fk: number;
  status_ids: number[];
  mapping_type?: 'direct';
}

// Get all funnel stages
export const getFunnelStages = async (): Promise<ApiResponse<{ data: FunnelStage[] }>> => {
  try {
    const response = await api.req<FunnelStage[]>({
      url: '/funnel/stages',
      method: api.Method.GET,
    });
    return response as any;
  } catch (error) {
    console.error('Error fetching funnel stages:', error);
    return {
      success: false,
      status: "error",
      message: "Failed to fetch funnel stages",
      data: { data: [] },
      totalPages: 0,
      total: 0
    } as ApiResponse<{ data: FunnelStage[] }>;
  }
};

// Get funnel stage by ID
export const getFunnelStageById = async (stageId: number): Promise<ApiResponse<{ data: FunnelStage }>> => {
  const response = await api.req<FunnelStage>({
    url: `/funnel/stages/detail/${stageId}`,
    method: api.Method.GET,
  });
  return response as any;
};

// Create funnel stage
export const createFunnelStage = async (data: FunnelStageFormData): Promise<ApiResponse<{ data: FunnelStage }>> => {
  const response = await api.req<FunnelStage>({
    url: '/funnel/stages',
    method: api.Method.POST,
    data,
  });
  return response as any;
};

// Update funnel stage
export const updateFunnelStage = async (
  stageId: number,
  data: FunnelStageFormData
): Promise<ApiResponse<{ data: FunnelStage }>> => {
  const response = await api.req<FunnelStage>({
    url: `/funnel/stages/${stageId}`,
    method: api.Method.PUT,
    data,
  });
  return response as any;
};

// Delete funnel stage
export const deleteFunnelStage = async (stageId: number): Promise<ApiResponse<void>> => {
  const response = await api.req<void>({
    url: `/funnel/stages/${stageId}`,
    method: api.Method.DELETE,
  });
  return response as any;
};

// Get available statuses for mapping
export const getAvailableStatuses = async (): Promise<ApiResponse<{ data: StatusOption[] }>> => {
  const response = await api.req<StatusOption[]>({
    url: '/funnel/available-statuses',
    method: api.Method.GET,
  });
  return response as any;
};

// Get unmapped statuses
export const getUnmappedStatuses = async (showAll: boolean = false, stageId?: number): Promise<ApiResponse<{ data: StatusOption[] }>> => {
  const params = new URLSearchParams({ showAll: String(showAll) });
  if (stageId !== undefined) {
    params.append('stage_id', String(stageId));
  }

  const response = await api.req<StatusOption[]>({
    url: `/funnel/unmapped-statuses?${params.toString()}`,
    method: api.Method.GET,
  });
  return response as any;
};

// Map single status to stage
export const mapStatusToStage = async (data: StatusMappingFormData): Promise<ApiResponse<{ data: StatusMapping }>> => {
  const response = await api.req<StatusMapping>({
    url: '/funnel/map-status',
    method: api.Method.POST,
    data,
  });
  return response as any;
};

// Map multiple statuses to stage
export const mapMultipleStatusesToStage = async (
  data: MultipleStatusMappingFormData
): Promise<ApiResponse<{ data: StatusMapping[] }>> => {
  const response = await api.req<StatusMapping[]>({
    url: '/funnel/map-multiple-statuses',
    method: api.Method.POST,
    data,
  });
  return response as any;
};

// Remove status mapping
export const removeStatusMapping = async (mappingId: number): Promise<ApiResponse<void>> => {
  const response = await api.req<void>({
    url: `/funnel/mapping/${mappingId}`,
    method: api.Method.DELETE,
  });
  return response as any;
};

// Manage stage mappings (add/remove multiple)
export const manageStageMappings = async (data: {
  stage_id: number;
  add_status_ids?: number[];
  remove_mapping_ids?: number[];
}): Promise<ApiResponse<void>> => {
  const response = await api.req<void>({
    url: '/funnel/manage-mappings',
    method: api.Method.POST,
    data,
  });
  return response as any;
};

// Generate funnel report
export const generateFunnelReport = async (data: {
  from_date?: string;
  to_date?: string;
}): Promise<ApiResponse<any>> => {
  const response = await api.req<any>({
    url: '/funnel/funnel-report',
    method: api.Method.POST,
    data,
  });
  return response as any;
};

// Analyze funnel progression
export const analyzeFunnelProgression = async (data: {
  from_date?: string;
  to_date?: string;
}): Promise<ApiResponse<any>> => {
  const response = await api.req<any>({
    url: '/funnel/progression-analysis',
    method: api.Method.POST,
    data,
  });
  return response as any;
};
